
import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  Award, 
  CheckCircle2, 
  XCircle, 
  RefreshCw, 
  BrainCircuit,
  FileSymlink,
  Lightbulb,
  Trophy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import SEO from "@/components/SEO";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

// Define the quiz question type
type QuizQuestion = {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
};

const SeoAssessmentTest = () => {
  const location = useLocation();
  const currentUrl = `https://losangelesseogroup.com${location.pathname}`;
  
  // Advanced SEO assessment questions
  const quizQuestions: QuizQuestion[] = [
    {
      id: 1,
      question: "Which HTTP status code indicates that a resource has been permanently moved to a new URL?",
      options: [
        "301",
        "302",
        "404",
        "500"
      ],
      correctAnswer: 0,
      explanation: "A 301 status code indicates a permanent redirect, telling search engines that a page has permanently moved to a new location and to transfer all ranking signals to the new URL."
    },
    {
      id: 2,
      question: "What is the optimal format for implementing structured data on a website?",
      options: [
        "Microdata",
        "RDFa",
        "JSON-LD",
        "Microformats"
      ],
      correctAnswer: 2,
      explanation: "JSON-LD is the recommended format by Google for implementing structured data as it doesn't need to be intertwined with your HTML content, making it easier to implement and maintain."
    },
    {
      id: 3,
      question: "In technical SEO, what does the 'rel=canonical' tag primarily help with?",
      options: [
        "Improving page load speed",
        "Preventing content duplication issues",
        "Enhancing mobile responsiveness",
        "Securing website connections"
      ],
      correctAnswer: 1,
      explanation: "The rel=canonical tag tells search engines which version of a page should be considered the 'master' copy, helping to prevent duplicate content issues when similar content exists on multiple URLs."
    },
    {
      id: 4,
      question: "Which of the following is NOT a component of Core Web Vitals?",
      options: [
        "First Input Delay (FID)",
        "Cumulative Layout Shift (CLS)",
        "Time to First Byte (TTFB)",
        "Largest Contentful Paint (LCP)"
      ],
      correctAnswer: 2,
      explanation: "Time to First Byte (TTFB) is an important performance metric but is not one of the three Core Web Vitals. The Core Web Vitals are LCP (loading), FID (interactivity), and CLS (visual stability)."
    },
    {
      id: 5,
      question: "What is the recommended approach for handling pagination in SEO?",
      options: [
        "Using rel='prev' and rel='next' attributes",
        "Implementing 'view-all' pages",
        "Using JavaScript-based infinite scroll",
        "Implementing a combination of rel='prev/next' with a proper internal linking structure"
      ],
      correctAnswer: 3,
      explanation: "While Google no longer officially supports rel='prev/next', the best practice is still to use these attributes combined with proper internal linking structure, as they help users navigate and can still be used by other search engines."
    },
    {
      id: 6,
      question: "Which of the following is a correct implementation of hreflang for targeting English speakers in the United States?",
      options: [
        "<link rel='alternate' hreflang='en' href='https://example.com/en/'>",
        "<link rel='alternate' hreflang='en-us' href='https://example.com/en-us/'>",
        "<link rel='alternate' hreflang='us' href='https://example.com/us/'>",
        "<link rel='alternate' hreflang='english-us' href='https://example.com/english-us/'>"
      ],
      correctAnswer: 1,
      explanation: "The correct hreflang attribute format combines language and region codes. For English speakers in the US, the correct format is 'en-us'. The language code 'en' alone doesn't specify a region."
    },
    {
      id: 7,
      question: "What does the robots.txt 'disallow: /' directive do?",
      options: [
        "Prevents search engines from indexing specific pages",
        "Blocks search engines from crawling the entire website",
        "Disallows users from accessing the site without authentication",
        "Prevents browser caching of the website content"
      ],
      correctAnswer: 1,
      explanation: "The directive 'disallow: /' tells search engine crawlers not to access any part of the website. This effectively blocks crawling of the entire site, though it doesn't necessarily prevent indexing if links to the site exist elsewhere."
    },
    {
      id: 8,
      question: "In the context of SEO, what is a 'Crawl Budget'?",
      options: [
        "The monthly fee paid to search engines for crawling a site",
        "The number of pages a search engine will crawl on your site in a given timeframe",
        "The maximum bandwidth allocated for search engine bots",
        "The SEO agency's budget allocation for crawl tools"
      ],
      correctAnswer: 1,
      explanation: "Crawl budget refers to the number of pages a search engine bot will crawl on your website within a certain timeframe. It's particularly important for large websites to optimize their crawl budget to ensure important pages are discovered and indexed."
    },
    {
      id: 9,
      question: "Which of the following is NOT considered a black hat SEO technique?",
      options: [
        "Keyword stuffing",
        "Cloaking",
        "Content siloing",
        "Private blog networks (PBNs)"
      ],
      correctAnswer: 2,
      explanation: "Content siloing is a legitimate SEO strategy that involves organizing your website content in a hierarchical structure to establish topical authority. The other options are black hat techniques that violate search engine guidelines."
    },
    {
      id: 10,
      question: "What is the primary purpose of implementing an XML sitemap?",
      options: [
        "To improve website loading speed",
        "To help search engines discover and index website pages",
        "To enhance user navigation experience",
        "To prevent content scraping"
      ],
      correctAnswer: 1,
      explanation: "An XML sitemap primarily helps search engines discover and crawl the pages on your website. It provides information about the structure of your site and the importance of each page, facilitating more efficient indexing."
    },
    {
      id: 11,
      question: "Which of the following best describes a 'Googlebomb'?",
      options: [
        "A malware that specifically targets Google users",
        "A technique where many sites link to a page with specific anchor text to manipulate rankings",
        "A sudden spike in website traffic from Google searches",
        "A Google algorithm update that significantly impacts rankings"
      ],
      correctAnswer: 1,
      explanation: "A Googlebomb is a technique where a large number of websites link to a particular page using specific anchor text, manipulating search rankings to associate that anchor text with the target page, often for humorous or political purposes."
    },
    {
      id: 12,
      question: "What is the correct order of heading tags for proper semantic structure?",
      options: [
        "H1 → H3 → H2 → H4",
        "H2 → H1 → H3 → H4",
        "H1 → H2 → H3 → H4",
        "The order doesn't matter as long as all headings are used"
      ],
      correctAnswer: 2,
      explanation: "For proper semantic structure, heading tags should follow a hierarchical order: H1 → H2 → H3 → H4, etc. This creates a logical document outline and helps both users and search engines understand the content structure."
    },
    {
      id: 13,
      question: "Which of the following is typically considered the strongest type of backlink?",
      options: [
        "A dofollow link from a high-authority site in your industry",
        "Multiple nofollow links from various blogs",
        "A link from a social media profile",
        "A link from a web directory"
      ],
      correctAnswer: 0,
      explanation: "A dofollow link from a high-authority website in your industry is typically the most valuable type of backlink. It passes the most link equity and signals to search engines that your content is endorsed by a trusted source in your niche."
    },
    {
      id: 14,
      question: "What is BERT in the context of Google's algorithm?",
      options: [
        "Bing's Exceptional Ranking Technology",
        "Bidirectional Encoder Representations from Transformers",
        "Basic Entity Recognition Technology",
        "Bounced Exit Rate Tracking"
      ],
      correctAnswer: 1,
      explanation: "BERT (Bidirectional Encoder Representations from Transformers) is a natural language processing model used by Google to better understand the context of words in search queries. It helps Google understand the nuances of natural language and the intent behind searches."
    },
    {
      id: 15,
      question: "What is the primary difference between 'index coverage' and 'crawl coverage'?",
      options: [
        "Index coverage relates to mobile devices; crawl coverage relates to desktops",
        "Index coverage shows which pages are in Google's index; crawl coverage shows which pages Google has crawled",
        "Index coverage is for on-page SEO; crawl coverage is for off-page SEO",
        "They are different terms for the same metric"
      ],
      correctAnswer: 1,
      explanation: "Index coverage refers to which pages have been added to Google's search index and can appear in search results. Crawl coverage refers to which pages Google's crawlers have visited. Pages can be crawled but not indexed for various reasons."
    },
    {
      id: 16,
      question: "What does a high 'cannibalization rate' indicate in SEO?",
      options: [
        "Your content is being stolen by competitor websites",
        "Multiple pages on your site are competing for the same keywords",
        "Your site has too many redirects affecting user experience",
        "Your site is consuming too many server resources"
      ],
      correctAnswer: 1,
      explanation: "Keyword cannibalization occurs when multiple pages on your site target the same keywords, causing them to compete against each other in search results. This can dilute your ranking potential and confuse search engines about which page to rank."
    },
    {
      id: 17,
      question: "In a URL structure, what is the difference between a subfolder and a subdomain?",
      options: [
        "Subfolders are used for blogs, while subdomains are used for e-commerce",
        "example.com/blog (subfolder) vs blog.example.com (subdomain)",
        "Subfolders require separate hosting; subdomains do not",
        "Subfolders cannot contain other folders; subdomains can"
      ],
      correctAnswer: 1,
      explanation: "A subfolder appears after the domain in the URL path (example.com/blog), while a subdomain appears before the main domain (blog.example.com). Search engines generally treat subdomains as separate websites, while subfolders are seen as part of the main domain."
    },
    {
      id: 18,
      question: "What is the E-A-T concept in SEO?",
      options: [
        "Engage, Attract, Transform – a content strategy framework",
        "Expertise, Authoritativeness, Trustworthiness – quality factors Google uses",
        "External, Authority, Trust – metrics for evaluating backlinks",
        "Evaluation, Analysis, Testing – steps in an SEO audit"
      ],
      correctAnswer: 1,
      explanation: "E-A-T stands for Expertise, Authoritativeness, and Trustworthiness. It's a concept from Google's Search Quality Evaluator Guidelines that helps assess the quality of web content, particularly important for YMYL (Your Money Your Life) topics."
    },
    {
      id: 19,
      question: "What is a 'featured snippet' in Google search results?",
      options: [
        "A paid advertisement at the top of search results",
        "A block at the top of results that directly answers a query",
        "A special link format that highlights trending topics",
        "Google's recommendation for the most reliable website"
      ],
      correctAnswer: 1,
      explanation: "A featured snippet is a selected search result that appears in a box at the top of Google's organic search results. It directly answers the user's question without requiring them to click through to a website, often called 'position zero'."
    },
    {
      id: 20,
      question: "Which of the following accurately describes the concept of 'link equity'?",
      options: [
        "The monetary value assigned to a backlink in paid link building",
        "The percentage of followed vs. nofollowed links to your site",
        "The value and authority that one webpage passes to another via links",
        "The ratio of internal to external links on a webpage"
      ],
      correctAnswer: 2,
      explanation: "Link equity (also called link juice) refers to the value and authority that one webpage passes to another through hyperlinks. Higher-authority pages pass more link equity, which can help boost the linked page's rankings."
    },
    {
      id: 21,
      question: "What is the purpose of implementing schema markup on a website?",
      options: [
        "To force Google to show rich snippets for your content",
        "To help search engines understand the context of your content",
        "To bypass the need for traditional SEO techniques",
        "To block certain users from accessing specific content"
      ],
      correctAnswer: 1,
      explanation: "Schema markup is a form of microdata that helps search engines understand the context and meaning of your content. While it doesn't guarantee rich snippets, it increases the chance that search engines will display your content in an enhanced way in search results."
    },
    {
      id: 22,
      question: "What does the 'noindex' meta tag do?",
      options: [
        "Prevents all bots from crawling the page",
        "Tells search engines not to include the page in search results",
        "Blocks specific IP addresses from accessing the page",
        "Prevents the page from being cached"
      ],
      correctAnswer: 1,
      explanation: "The 'noindex' meta tag instructs search engines not to include the page in their index, meaning it won't appear in search results. This is different from 'nofollow' which affects whether links on the page are followed."
    },
    {
      id: 23,
      question: "What is 'topic authority' in SEO?",
      options: [
        "A plugin that helps identify authoritative content",
        "The perceived expertise of a website on a specific subject matter",
        "A ranking factor that considers how many topics a site covers",
        "A metric that measures topic popularity in search queries"
      ],
      correctAnswer: 1,
      explanation: "Topic authority refers to how much expertise and authority a website demonstrates on a specific subject. Sites with high topic authority are more likely to rank well for related queries, as search engines see them as valuable resources on that subject."
    },
    {
      id: 24,
      question: "Which of the following is NOT a component of technical SEO?",
      options: [
        "XML sitemap optimization",
        "Site speed optimization",
        "Content keyword placement",
        "Implementing proper redirects"
      ],
      correctAnswer: 2,
      explanation: "Content keyword placement falls under on-page SEO rather than technical SEO. Technical SEO focuses on website and server optimizations that help search engine spiders crawl and index your site more effectively, such as site architecture, speed, and technical implementations."
    },
    {
      id: 25,
      question: "What is the purpose of Google's 'nofollow', 'sponsored', and 'ugc' link attributes?",
      options: [
        "They tell Google to index these links more frequently",
        "They categorize links for search engines to understand their nature",
        "They highlight these links for users with accessibility needs",
        "They prevent search engines from discovering the linked pages"
      ],
      correctAnswer: 1,
      explanation: "These link attributes help Google understand the nature of links: 'nofollow' indicates a link that shouldn't pass authority, 'sponsored' indicates a paid or sponsored link, and 'ugc' indicates user-generated content links. This helps Google better understand the link graph and use links as ranking signals more appropriately."
    }
  ];

  // State variables
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [answers, setAnswers] = useState<number[]>([]);
  const [timeSpent, setTimeSpent] = useState(0);
  const [timerInterval, setTimerInterval] = useState<number | null>(null);

  // Start timer when component mounts
  React.useEffect(() => {
    const interval = window.setInterval(() => {
      setTimeSpent(prev => prev + 1);
    }, 1000);
    
    setTimerInterval(interval);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  // Format time for display
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Handle answer selection
  const handleAnswerSelect = (answerIndex: number) => {
    if (selectedAnswerIndex === null) {
      setSelectedAnswerIndex(answerIndex);
      const isCorrect = answerIndex === quizQuestions[currentQuestionIndex].correctAnswer;
      if (isCorrect) {
        setScore(score + 1);
      }
      
      // Save this answer
      const newAnswers = [...answers];
      newAnswers[currentQuestionIndex] = answerIndex;
      setAnswers(newAnswers);
      
      // Show explanation after selection
      setShowExplanation(true);
    }
  };

  // Move to the next question
  const handleNextQuestion = () => {
    if (currentQuestionIndex < quizQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswerIndex(null);
      setShowExplanation(false);
    } else {
      setQuizCompleted(true);
      // Stop timer
      if (timerInterval) clearInterval(timerInterval);
      // Show completion message
      toast.success("Assessment completed! See your results below.");
    }
  };

  // Restart the quiz
  const restartQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswerIndex(null);
    setScore(0);
    setShowExplanation(false);
    setQuizCompleted(false);
    setAnswers([]);
    setTimeSpent(0);
    
    // Restart timer
    if (timerInterval) clearInterval(timerInterval);
    const interval = window.setInterval(() => {
      setTimeSpent(prev => prev + 1);
    }, 1000);
    setTimerInterval(interval);
  };

  // Get appropriate icon and style for result
  const getResultIcon = (score: number) => {
    const percentage = (score / quizQuestions.length) * 100;
    
    if (percentage >= 80) {
      return <Trophy className="h-16 w-16 text-yellow-500" />;
    } else if (percentage >= 60) {
      return <Award className="h-16 w-16 text-blue-500" />;
    } else if (percentage >= 40) {
      return <FileSymlink className="h-16 w-16 text-purple-500" />;
    } else {
      return <Lightbulb className="h-16 w-16 text-orange-500" />;
    }
  };

  // Get result message based on score
  const getResultMessage = (score: number) => {
    const percentage = (score / quizQuestions.length) * 100;
    
    if (percentage >= 80) {
      return "SEO Master! Your in-depth knowledge is impressive!";
    } else if (percentage >= 60) {
      return "SEO Professional! You have solid advanced knowledge.";
    } else if (percentage >= 40) {
      return "SEO Practitioner! You understand many advanced concepts.";
    } else {
      return "SEO Student! This assessment revealed areas for deeper study.";
    }
  };

  // Calculate progress percentage
  const progressPercentage = ((currentQuestionIndex + 1) / quizQuestions.length) * 100;

  return (
    <>
      <SEO 
        seo={{
          title: "Advanced SEO Assessment Test | Test Your In-depth SEO Knowledge",
          description: "Challenge yourself with our advanced 25-question SEO assessment test designed for SEO professionals and experts."
        }}
        url={currentUrl}
      />
      <Navbar />
      <PageTransition>
        <main className="page-container section-padding py-12 min-h-[80vh]">
          <div className="max-w-3xl mx-auto">
            {!quizCompleted ? (
              <>
                <div className="mb-8">
                  <h1 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                    Advanced SEO Assessment Test
                  </h1>
                  <p className="text-muted-foreground text-center mb-6">
                    Challenge yourself with these 25 in-depth questions designed to test advanced SEO knowledge.
                  </p>
                  <div className="mb-4">
                    <Progress value={progressPercentage} className="h-2" />
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-sm text-muted-foreground">
                        Question {currentQuestionIndex + 1} of {quizQuestions.length}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Time: {formatTime(timeSpent)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card border rounded-lg shadow-sm p-6 mb-8">
                  <h2 className="text-xl font-semibold mb-4">
                    {quizQuestions[currentQuestionIndex].question}
                  </h2>
                  
                  <div className="space-y-3">
                    {quizQuestions[currentQuestionIndex].options.map((option, index) => (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <button
                          className={`w-full text-left p-4 rounded-md border transition-colors flex items-center space-x-3 ${
                            selectedAnswerIndex === index 
                              ? index === quizQuestions[currentQuestionIndex].correctAnswer
                                ? "bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-800"
                                : "bg-red-100 border-red-300 dark:bg-red-900/30 dark:border-red-800"
                              : "hover:bg-muted"
                          }`}
                          onClick={() => handleAnswerSelect(index)}
                          disabled={selectedAnswerIndex !== null}
                        >
                          {selectedAnswerIndex === index ? (
                            index === quizQuestions[currentQuestionIndex].correctAnswer ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                            )
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 flex-shrink-0"></div>
                          )}
                          <span>{option}</span>
                        </button>
                      </motion.div>
                    ))}
                  </div>

                  {showExplanation && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="mt-6 p-4 bg-muted/50 rounded-md"
                    >
                      <h3 className="font-medium mb-2">Explanation:</h3>
                      <p>{quizQuestions[currentQuestionIndex].explanation}</p>
                    </motion.div>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleNextQuestion}
                    disabled={selectedAnswerIndex === null}
                    size="lg"
                  >
                    {currentQuestionIndex < quizQuestions.length - 1 ? "Next Question" : "See Results"}
                  </Button>
                </div>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center"
              >
                <div className="mb-8">
                  <div className="flex justify-center mb-4">
                    {getResultIcon(score)}
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold mb-2">
                    {getResultMessage(score)}
                  </h1>
                  <p className="text-xl mb-2">
                    You scored <span className="font-bold">{score}</span> out of {quizQuestions.length}
                  </p>
                  <p className="text-muted-foreground">
                    Total time: {formatTime(timeSpent)}
                  </p>
                </div>

                <div className="bg-card border rounded-lg shadow-sm p-6 mb-8">
                  <h2 className="text-xl font-semibold mb-4">Your Answers</h2>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {quizQuestions.map((question, index) => (
                      <div key={index} className="border-b pb-4 last:border-b-0 text-left">
                        <div className="flex items-start gap-3">
                          {answers[index] === question.correctAnswer ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-1 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-1 flex-shrink-0" />
                          )}
                          <div>
                            <p className="font-medium">{question.question}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Your answer: <span className={answers[index] === question.correctAnswer ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                                {question.options[answers[index]]}
                              </span>
                            </p>
                            {answers[index] !== question.correctAnswer && (
                              <p className="text-sm text-muted-foreground">
                                Correct answer: <span className="text-green-600 dark:text-green-400">
                                  {question.options[question.correctAnswer]}
                                </span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button 
                    onClick={restartQuiz}
                    variant="outline"
                    size="lg"
                    className="flex gap-2 items-center"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    className="flex gap-2 items-center"
                  >
                    <a href="/knowledge-base">
                      <BrainCircuit className="h-4 w-4" />
                      Study SEO Resources
                    </a>
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </main>
      </PageTransition>
      <Footer />
    </>
  );
};

export default SeoAssessmentTest;
